import { useQueryClient } from '@tanstack/react-query';
import { useCompleteOnboardingStepMutation } from 'hooks/mutations/useCompleteOnboardingStepMutation';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useWaitForPlanUpdate } from 'hooks/useWaitForPlanUpdate';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import PageLoader from 'components/PageLoader';
import { useBilling } from 'features/billing/context/BillingProvider';
import { ERoute, TanstackQueryName } from 'types/frontend';

const StripeCallback = () => {
  const { toastInfo } = useToastWrapper();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { cacheTimestamp, clearStorage } = useBilling();
  const [searchParams] = useSearchParams();
  const { mutateAsync: completeOnboardingStep } = useCompleteOnboardingStepMutation();
  const hasRun = useRef(false);
  const hasFinalized = useRef(false);
  const [readyToWait, setReadyToWait] = useState(false);

  const isOnboarding = searchParams.get('onboarding') === 'true';
  const type = searchParams.get('type');
  const path = searchParams.get('path');
  const isTrial = searchParams.get('trial') === 'true';

  // Payment flows that update user.plan: subscription (monthly/yearly) or lifetime payment (no path param)
  const isPaymentFlow = type === 'subscription' || (type === 'payment' && !path);

  const { didTimeout, hasPollingResolved } = useWaitForPlanUpdate({
    enabled: readyToWait && isPaymentFlow,
  });

  // Effect 1: Handle initial actions (cache only — NOT onboarding step completion)
  useEffect(() => {
    const handleCallback = async () => {
      if (hasRun.current) return;
      hasRun.current = true;

      if (type === 'subscription') {
        cacheTimestamp();
      }

      // For payment method changes, navigate immediately (no plan update expected)
      if (type === 'change') {
        toastInfo('Payment method change attempt registered');
        navigate(ERoute.Billing);
        return;
      }

      // For token package purchases (has path), navigate immediately
      if (type === 'payment' && path) {
        toastInfo('Payment registered.');
        navigate(path);
        return;
      }

      // For subscription/lifetime, start polling for plan update
      setReadyToWait(true);
    };

    handleCallback();
  }, []);

  // Effect 2: Navigate once polling completes (plan updated or timeout)
  // Guard: hasPollingResolved prevents premature finalization from the initial
  // isWaiting=false (useState default) before the hook's useEffect sets isWaiting=true.
  useEffect(() => {
    if (!readyToWait || !hasPollingResolved || hasFinalized.current) return;
    hasFinalized.current = true;

    if (didTimeout) {
      // Webhook didn't arrive — keep user on onboarding so they can retry
      toastInfo('Payment is being processed. Please wait a moment and try again.');

      if (isOnboarding) {
        navigate(ERoute.Onboarding);
      } else {
        navigate(ERoute.Plans);
      }
      return;
    }

    // Plan updated successfully — now complete onboarding step (sets onboarding_step = null)
    // This MUST happen after plan is confirmed so Routes.tsx sees paid user, not free user
    const finalize = async () => {
      if (isOnboarding) {
        try {
          await completeOnboardingStep();
          // Wait for user data to refresh so Routes.tsx sees updated plan + onboarding_step
          // Without this, there's a flash of /plans from stale isFree=true in cache
          await queryClient.refetchQueries({ queryKey: [TanstackQueryName.GetCurrentUser] });
        } catch (error) {
          console.error('Failed to complete onboarding step:', error);
        }
      }

      clearStorage();

      if (isOnboarding) {
        // Navigate to dashboard with welcome flag — WelcomeModal will show there
        const welcomeParams = new URLSearchParams({ welcome: 'true' });
        if (isTrial) welcomeParams.set('trial', 'true');
        navigate(`${ERoute.Dashboard}?${welcomeParams.toString()}`);
      } else {
        toastInfo('Payment registered.');
        navigate(ERoute.Billing);
      }
    };

    finalize();
  }, [readyToWait, hasPollingResolved]);

  return (
    <PageLoader
      shift={!isOnboarding}
      text={isOnboarding ? 'Finalizing your account...' : 'Processing your payment...'}
    />
  );
};

export default StripeCallback;
